// PDF Generation Utility
// Generates PDF from the actual visible DOM element and saves to backend
// WYSIWYG approach - captures exactly what the user sees

import html2pdf from 'html2pdf.js';
import { PDFDocument } from 'pdf-lib';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Prepare an element for PDF capture by converting form inputs to visible text
 * @param {HTMLElement} clonedElement - The cloned container element
 */
const prepareForPDFCapture = (clonedElement) => {
  // Convert textarea values to preserve line breaks
  const textareas = clonedElement.querySelectorAll('textarea');
  textareas.forEach(textarea => {
    const value = textarea.value || textarea.textContent || '';
    const displayDiv = document.createElement('div');
    displayDiv.style.cssText = `
      white-space: pre-wrap;
      word-wrap: break-word;
      padding: 12px 16px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      background-color: #f9fafb;
      font-size: 14px;
      line-height: 1.5;
      min-height: ${textarea.rows ? textarea.rows * 24 : 100}px;
    `;
    displayDiv.textContent = value;
    textarea.parentNode.replaceChild(displayDiv, textarea);
  });

  // Convert input values to visible text
  const inputs = clonedElement.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]');
  inputs.forEach(input => {
    const value = input.value || '';
    const displaySpan = document.createElement('span');
    displaySpan.style.cssText = `
      display: block;
      padding: 8px 16px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      background-color: #f9fafb;
      font-size: 14px;
      min-height: 40px;
      line-height: 24px;
    `;
    displaySpan.textContent = value || input.placeholder || '';
    if (!value) {
      displaySpan.style.color = '#9ca3af';
    }
    input.parentNode.replaceChild(displaySpan, input);
  });

  // Handle Select/dropdown components - find the displayed value
  const selectTriggers = clonedElement.querySelectorAll('[data-radix-collection-item], [role="combobox"], button[type="button"]');
  selectTriggers.forEach(trigger => {
    // Check if this is a select trigger by looking for SelectValue content
    const valueSpan = trigger.querySelector('span');
    if (valueSpan && trigger.closest('[class*="SelectTrigger"]')) {
      const value = valueSpan.textContent || 'Not selected';
      const displaySpan = document.createElement('span');
      displaySpan.style.cssText = `
        display: block;
        padding: 8px 16px;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        background-color: #f9fafb;
        font-size: 14px;
        min-height: 40px;
        line-height: 24px;
      `;
      displaySpan.textContent = value;
      trigger.parentNode.replaceChild(displaySpan, trigger);
    }
  });

  // Remove elements that shouldn't appear in PDF
  const selectorsToRemove = [
    'button',
    '[data-hide-in-pdf="true"]',
    '.page-indicator',
    '[class*="tooltip"]',
    '[class*="popover"]',
    '[role="tooltip"]',
  ];
  
  selectorsToRemove.forEach(selector => {
    const elements = clonedElement.querySelectorAll(selector);
    elements.forEach(el => el.remove());
  });

  // Remove text patterns
  const textPatternsToRemove = [
    'Use your mouse or finger to sign above',
    'Clear Signature',
    'Go to Next Page',
    'Back to Page',
    '← Back',
    'Saving...'
  ];

  const allElements = clonedElement.querySelectorAll('p, span, small');
  allElements.forEach(el => {
    const text = el.textContent?.trim() || '';
    if (textPatternsToRemove.some(pattern => text === pattern)) {
      el.remove();
    }
  });

  // Remove address tip/tooltip
  const addressTips = clonedElement.querySelectorAll('[class*="blue-50"], [class*="tooltip"], [class*="tip"]');
  addressTips.forEach(tip => tip.remove());
};

/**
 * Remove blank pages from PDF
 * @param {string} pdfBase64 - Base64 encoded PDF
 * @returns {Promise<string>} - Base64 encoded PDF without blank pages
 */
const removeBlankPages = async (pdfBase64) => {
  try {
    let base64Data = pdfBase64;
    if (pdfBase64.startsWith('data:application/pdf;base64,')) {
      base64Data = pdfBase64.split(',')[1];
    }
    
    const pdfBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
    const pdfDoc = await PDFDocument.load(pdfBytes);
    
    const pages = pdfDoc.getPages();
    const pagesToRemove = [];
    
    for (let i = pages.length - 1; i >= 0; i--) {
      const page = pages[i];
      const contentStream = page.node.Contents();
      
      if (contentStream) {
        const contents = contentStream.toString();
        if (contents.length < 200) {
          pagesToRemove.push(i);
          console.log(`[PDF] Marking page ${i + 1} as blank`);
        }
      }
    }
    
    for (const pageIndex of pagesToRemove) {
      pdfDoc.removePage(pageIndex);
    }
    
    if (pdfDoc.getPageCount() === 0) {
      return pdfBase64;
    }
    
    const modifiedPdfBytes = await pdfDoc.save();
    const modifiedBase64 = btoa(String.fromCharCode(...modifiedPdfBytes));
    
    return `data:application/pdf;base64,${modifiedBase64}`;
  } catch (error) {
    console.error('[PDF] Error removing blank pages:', error);
    return pdfBase64;
  }
};

/**
 * Generate PDF from the current page - WYSIWYG approach
 * @param {string} formId - Unique form session ID
 * @param {number} pageNumber - Page number (1-7)
 * @param {string} pageTitle - Title for the PDF page
 * @param {object} clientData - Current client data snapshot
 * @param {string} elementSelector - CSS selector for the element to capture
 * @returns {Promise<{success: boolean, pdfSize: number, error?: string}>}
 */
export const generateAndSavePagePDF = async (formId, pageNumber, pageTitle, clientData, elementSelector = '#page-pdf-content') => {
  let offscreenContainer = null;
  
  try {
    console.log(`[PDF] Starting PDF generation for page ${pageNumber}`);
    
    const originalElement = document.querySelector(elementSelector);
    if (!originalElement) {
      console.error(`[PDF] Element not found: ${elementSelector}`);
      return { success: false, error: `Element not found: ${elementSelector}` };
    }

    console.log(`[PDF] Found element, creating off-screen clone...`);

    // Create off-screen container
    offscreenContainer = document.createElement('div');
    offscreenContainer.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: 794px;
      background-color: #ffffff;
      z-index: -1;
      visibility: visible;
      opacity: 1;
      padding: 20px;
    `;
    document.body.appendChild(offscreenContainer);

    // Clone the original element
    const clonedElement = originalElement.cloneNode(true);
    clonedElement.style.backgroundColor = '#ffffff';
    clonedElement.style.width = '100%';
    offscreenContainer.appendChild(clonedElement);

    // Prepare the clone for PDF (convert inputs to text, remove buttons)
    prepareForPDFCapture(clonedElement);

    // Wait for images to load
    const images = offscreenContainer.querySelectorAll('img');
    await Promise.all(Array.from(images).map(img => {
      return new Promise(resolve => {
        if (img.complete) resolve();
        else {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 3000);
        }
      });
    }));
    
    await new Promise(resolve => setTimeout(resolve, 300));

    console.log(`[PDF] Clone prepared, generating PDF...`);

    // PDF options optimized for compact output
    const options = {
      margin: [5, 5, 5, 5],
      filename: `page_${pageNumber}.pdf`,
      image: { type: 'jpeg', quality: 0.92 },
      html2canvas: { 
        scale: 1.5,
        useCORS: true, 
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 10000,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { 
        mode: ['avoid-all', 'css'],
        avoid: ['img', '.avoid-break', 'table']
      }
    };

    let pdfData = await html2pdf()
      .set(options)
      .from(offscreenContainer)
      .outputPdf('datauristring');

    console.log(`[PDF] Initial PDF generated, size: ${pdfData.length} chars`);

    // Remove blank pages
    pdfData = await removeBlankPages(pdfData);
    console.log(`[PDF] After blank page removal, size: ${pdfData.length} chars`);

    // Save to backend
    const response = await fetch(`${BACKEND_URL}/api/pdf/save-page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        form_id: formId,
        page_number: pageNumber,
        pdf_data: pdfData,
        page_title: pageTitle,
        client_data: clientData
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[PDF] Backend save failed: ${response.status} - ${errorText}`);
      return { success: false, error: `Backend save failed: ${response.status}` };
    }

    const result = await response.json();
    console.log(`[PDF] Saved to backend - Page ${pageNumber}, size: ${result.pdf_size} bytes`);

    return { success: true, pdfSize: result.pdf_size, id: result.id };

  } catch (error) {
    console.error(`[PDF] Error generating/saving PDF for page ${pageNumber}:`, error);
    return { success: false, error: error.message };
  } finally {
    if (offscreenContainer && offscreenContainer.parentNode) {
      offscreenContainer.parentNode.removeChild(offscreenContainer);
    }
  }
};

/**
 * Get all saved PDFs for a form
 */
export const getSavedPDFs = async (formId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/pdf/get-pages/${formId}`);
    if (!response.ok) throw new Error(`Failed to fetch PDFs: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error(`[PDF] Error fetching saved PDFs:`, error);
    throw error;
  }
};

/**
 * Debug - get info about saved PDFs without data
 */
export const debugSavedPDFs = async (formId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/pdf/debug/${formId}`);
    if (!response.ok) throw new Error(`Failed to debug PDFs: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error(`[PDF] Error debugging PDFs:`, error);
    throw error;
  }
};

/**
 * Generate a unique form ID
 */
export const generateFormId = () => {
  return `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
